import { MapPin } from 'lucide-react';
import React, { FC } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Error, LightboxGallery, Spinner } from 'src/components';
import { useGetAttractionFromFirestore, useQueryHelper } from 'src/queries';
import { useLanguageState } from 'src/state';

import {
  Address,
  Banner,
  Button,
  ButtonText,
  ContentContainer,
  Description,
  DiscountCode,
  DiscountCodeContainer,
  DiscountCodeValue,
  DiscountContainer,
  DiscountLabel,
  DiscountLabelValue,
  DiscountTextContainer,
  DiscountTitleValue,
  Label,
  LocationButton,
  MapContainer,
  Title,
  WebsiteContainer,
  WebsiteTitleContainer,
  WebsiteUrlContainer,
} from './Attraction.styled';

export const Attraction: FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { language: currentLang } = useLanguageState();
  const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
  const AttractionDocument = useGetAttractionFromFirestore(id || '');
  const { data: AttractionData, refetch } = AttractionDocument;
  const { isLoading, isError } = useQueryHelper({ queries: [AttractionDocument] });

  if (isLoading) {
    return <Spinner global />;
  }

  if (isError) {
    return (<Error errorMessage="Bład pobierania atrakcji" action={refetch} />
    );
  }

  return (
    <>
      <Helmet>
        <title>Hotelspot | Szczegóły atrakcji</title>
        <meta name="description" content="Hotelspot - informacje hotelowe" />
      </Helmet>

      <ContentContainer>
        <Banner src={AttractionData?.cover_photo || ''} height="4x00px" />
        <Title>{AttractionData?.title[currentLang]}</Title>
        <Label>
          <MapPin size={20} color="#1C1C1C" />
          <Address>
            {AttractionData?.address}
          </Address>
        </Label>
        <Description>{AttractionData?.description?.[currentLang]}</Description>
      </ContentContainer>
      {!!AttractionData?.web_url && (
        <ContentContainer>
          <WebsiteContainer>
            <WebsiteTitleContainer>
              <Title>{t('attraction_screen.website_title')}</Title>
              <Description>
                {t('attraction_screen.website_description')}
              </Description>
            </WebsiteTitleContainer>
            <WebsiteUrlContainer>
              <Button
                target="_blank"
                href={AttractionData?.web_url}
              >
                <ButtonText>
                  {t('buttons.show_more')}
                </ButtonText>
              </Button>
            </WebsiteUrlContainer>
          </WebsiteContainer>
        </ContentContainer>
      )}

      {!!AttractionData?.discount?.discount_code && (

        <ContentContainer>
          <Title>{AttractionData?.discount?.title[currentLang]}</Title>
          <DiscountContainer>
            <DiscountCode>
              <DiscountLabelValue>{AttractionData?.discount?.value}%</DiscountLabelValue>
              <DiscountLabel>KOD</DiscountLabel>
            </DiscountCode>
            <DiscountTextContainer>
              <DiscountTitleValue>{AttractionData?.discount?.title[currentLang]}</DiscountTitleValue>
              <p>{AttractionData?.discount?.description[currentLang]}</p>
            </DiscountTextContainer>
            <DiscountCodeContainer>
              <DiscountCodeValue>{AttractionData?.discount?.discount_code}</DiscountCodeValue>
            </DiscountCodeContainer>
          </DiscountContainer>
        </ContentContainer>
      )}
      {!!AttractionData?.gallery?.length && (
        <ContentContainer>
          <Title>{t('attraction_screen.gallery')}</Title>
          <LightboxGallery urls={AttractionData?.gallery || []} />
        </ContentContainer>
      )}

      <ContentContainer>
        <Title>{t('attraction_screen.route')}</Title>
        <MapContainer>
          <iframe
            title="maps"
            width="100%"
            loading="lazy"
            src={`https://www.google.com/maps/embed/v1/place?key=${API_KEY}&q=${AttractionData?.localization?.latitude},${AttractionData?.localization?.longitude}`}
          />
          <LocationButton
            target="_blank"
            href={`https://www.google.com/maps?q=${AttractionData?.localization?.latitude},${AttractionData?.localization?.longitude}`}
          >
            <ButtonText>
              {t('map_screen.get_route')}
            </ButtonText>
          </LocationButton>
        </MapContainer>
      </ContentContainer>

    </>
  );
};
